import React from 'react';
import { StatCard, EmptyState, AssessmentPill, OutsidePeriodBanner } from './shared';
import DevelopmentTargetBar from './DevelopmentTargetBar';
import { fmtNum, fmtDate } from './constants';
import { Video, ChevronUp, ChevronDown, Plus, Check } from 'lucide-react';

// ── Overview (identity page) ───────────────────────────────────────────────
export const OverviewSection = ({ data, meta = {} }) => {
  if (!data) return <EmptyState message="Athlete overview unavailable." />;
  const photo = data.photo_url;
  const rows = [
    ['Position', data.position],
    ['Squad / Age Group', data.squad],
    ['Date of Birth', fmtDate(data.date_of_birth)],
    ['Age at Report', data.age_at_report != null ? `${data.age_at_report} yrs` : null],
    ['Nationality', data.nationality],
    ['Review Period', `${fmtDate(data.review_start_date)} – ${fmtDate(data.review_end_date)}`],
    ['Report Date', fmtDate(data.report_date)],
    ['Lead Staff', meta.author || null],
  ];
  return (
    <div className="flex flex-col sm:flex-row gap-5" data-testid="section-overview">
      <div className="shrink-0">
        {photo ? (
          <img src={photo} alt={data.full_name} className="h-28 w-28 rounded-2xl object-cover border border-slate-200" />
        ) : (
          <div className="h-28 w-28 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center text-3xl font-semibold text-slate-400">
            {(data.full_name || '?').slice(0, 1)}
          </div>
        )}
      </div>
      <div className="flex-1">
        <h2 className="text-2xl font-semibold text-slate-800">{data.full_name}</h2>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          {rows.filter(([, v]) => v).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-slate-100 py-1.5">
              <span className="text-sm text-slate-500">{k}</span>
              <span className="text-sm font-medium text-slate-800 text-right">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Football Performance ────────────────────────────────────────────────────
const IN_POSSESSION = [
  ['passes_completed', 'Passes completed'], ['passes_attempted', 'Passes attempted'],
  ['pass_completion_pct', 'Pass completion', '%'], ['progressive_passes', 'Progressive passes'],
  ['key_passes', 'Key passes'], ['chances_created', 'Chances created'],
  ['dribbles_successful', 'Successful dribbles'], ['dribble_success_pct', 'Dribble success', '%'],
  ['progressive_carries', 'Progressive carries'], ['shots', 'Shots'],
  ['shots_on_target', 'Shots on target'], ['shot_accuracy_pct', 'Shot accuracy', '%'],
  ['crosses', 'Crosses'],
];
const OUT_POSSESSION = [
  ['tackles_won', 'Tackles won'], ['tackle_success_pct', 'Tackle success', '%'],
  ['interceptions', 'Interceptions'], ['recoveries', 'Recoveries'],
  ['defensive_duels_won', 'Defensive duels won'], ['defensive_duel_win_pct', 'Defensive duel win', '%'],
  ['aerial_duels_won', 'Aerial duels won'], ['aerial_duel_win_pct', 'Aerial duel win', '%'],
  ['blocks', 'Blocks'], ['clearances', 'Clearances'], ['pressing_actions', 'Pressing actions'],
];

const StatRow = ({ label, value, unit }) => {
  if (value === null || value === undefined) return null; // hide un-recorded (§16)
  return (
    <div className="flex justify-between border-b border-slate-100 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800">{fmtNum(value, unit === '%' ? 1 : null)}{unit === '%' ? '%' : ''}</span>
    </div>
  );
};

export const FootballSection = ({ data, showMatches = true }) => {
  if (!data) return <EmptyState message="No matches recorded during this review period." />;
  const d = data;
  const det = d.detailed_stats || {};
  const merged = { ...(det.technical || {}), ...(det.defensive || {}), ...(det.derived || {}) };
  const inRows = IN_POSSESSION.filter(([k]) => merged[k] !== null && merged[k] !== undefined);
  const outRows = OUT_POSSESSION.filter(([k]) => merged[k] !== null && merged[k] !== undefined);
  return (
    <div className="space-y-5" data-testid="section-football">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Appearances" value={d.appearances} testId="fb-appearances" />
        <StatCard label="Matches" value={d.matches} testId="fb-matches" />
        <StatCard label="Minutes" value={d.minutes} testId="fb-minutes" />
        <StatCard label="Goals" value={d.goals} testId="fb-goals" />
        <StatCard label="Assists" value={d.assists} testId="fb-assists" />
        <StatCard label="Avg Rating" value={d.average_rating} testId="fb-rating" />
      </div>
      <div className="flex flex-wrap gap-2 text-sm text-slate-600">
        {d.positions?.length > 0 && <span className="rounded-full bg-slate-100 px-3 py-1">Positions: {d.positions.join(', ')}</span>}
        {(d.yellow_cards != null) && <span className="rounded-full bg-amber-50 text-amber-700 px-3 py-1">Yellow: {d.yellow_cards}</span>}
        {(d.red_cards != null) && <span className="rounded-full bg-rose-50 text-rose-700 px-3 py-1">Red: {d.red_cards}</span>}
      </div>

      {(inRows.length > 0 || outRows.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {inRows.length > 0 && (
            <div>
              <h4 className="mb-1.5 text-sm font-semibold text-slate-700">In Possession</h4>
              {inRows.map(([k, l, u]) => <StatRow key={k} label={l} value={merged[k]} unit={u} />)}
            </div>
          )}
          {outRows.length > 0 && (
            <div>
              <h4 className="mb-1.5 text-sm font-semibold text-slate-700">Out of Possession</h4>
              {outRows.map(([k, l, u]) => <StatRow key={k} label={l} value={merged[k]} unit={u} />)}
            </div>
          )}
        </div>
      )}

      {showMatches && d.match_by_match?.length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium text-slate-600">Matches contributing to this report ({d.match_by_match.length})</summary>
          <div className="mt-2 space-y-1">
            {d.match_by_match.map((m, i) => (
              <div key={i} className="flex flex-wrap justify-between gap-2 border-b border-slate-100 py-1.5 text-sm">
                <span className="text-slate-700">{fmtDate(m.match_date)} · {m.opponent || 'Opponent'}</span>
                <span className="text-slate-500">{m.position || '—'} · {fmtNum(m.minutes_played)}'{m.rating != null ? ` · ${m.rating}` : ''}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

// ── Match Analysis & Video Evidence ─────────────────────────────────────────
// Interactive in builder (include/exclude/reorder). `included` is a Set of
// source_ids; when `readOnly` (preview) only included observations render.
export const MatchAnalysisSection = ({ data, included = null, onToggle = null, onMove = null, readOnly = false }) => {
  if (!data) return <EmptyState message="No match observations recorded during this review period." />;
  const obs = data.observations || [];

  // Development tag frequency (descriptive only — §19).
  const tagCounts = {};
  obs.forEach((o) => (o.development_tags || []).forEach((t) => {
    if (!t) return; tagCounts[t.label] = (tagCounts[t.label] || 0) + 1;
  }));
  const topTags = Object.entries(tagCounts).sort((a, b) => b[1] - a[1]).slice(0, 8);

  let list = obs;
  if (readOnly) list = obs.filter((o) => included && included.has(o.id));

  return (
    <div className="space-y-4" data-testid="section-match-analysis">
      <div className="flex flex-wrap gap-3 text-sm">
        <span className="rounded-full bg-emerald-50 text-emerald-700 px-3 py-1">{data.positive || 0} positive</span>
        <span className="rounded-full bg-sky-50 text-sky-700 px-3 py-1">{data.needs_improvement || 0} development</span>
        <span className="rounded-full bg-slate-100 text-slate-600 px-3 py-1">{data.count || obs.length} total observations</span>
      </div>

      {topTags.length > 0 && (
        <div>
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">Development tag patterns (observation counts)</div>
          <div className="flex flex-wrap gap-2">
            {topTags.map(([label, n]) => (
              <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-600">
                {label}<span className="rounded-full bg-slate-100 px-1.5 font-medium text-slate-700">{n}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {!readOnly && (
        <p className="text-xs text-slate-400">Toggle which observations are <strong>included in the report</strong>. Included evidence appears in the player/parent view.</p>
      )}

      <div className="space-y-2">
        {list.length === 0 && <EmptyState message={readOnly ? 'No evidence selected for this section.' : 'No observations in range.'} />}
        {list.map((o, idx) => {
          const inc = included ? included.has(o.id) : false;
          return (
            <div key={o.id || idx} className={`rounded-lg border px-3 py-2.5 ${inc ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`} data-testid={`observation-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <AssessmentPill assessment={o.assessment} />
                    {o.timestamp && <span className="text-xs text-slate-400">{o.timestamp}</span>}
                    {o.category && <span className="text-xs text-slate-400">· {o.category}</span>}
                    {o.evidence_url && (
                      <a href={o.evidence_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-sky-600 hover:underline" data-testid={`observation-veo-${o.id}`}>
                        <Video className="h-3 w-3" /> View evidence
                      </a>
                    )}
                  </div>
                  {o.text && <p className="text-sm text-slate-700">{o.text}</p>}
                  {o.development_tags?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {o.development_tags.map((t) => t && (
                        <span key={t.id} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">{t.label}</span>
                      ))}
                    </div>
                  )}
                </div>
                {!readOnly && (
                  <div className="flex shrink-0 items-center gap-1">
                    {inc && onMove && (
                      <div className="flex flex-col">
                        <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => onMove(o.id, -1)} data-testid={`evidence-up-${o.id}`}><ChevronUp className="h-4 w-4" /></button>
                        <button type="button" className="text-slate-400 hover:text-slate-700" onClick={() => onMove(o.id, 1)} data-testid={`evidence-down-${o.id}`}><ChevronDown className="h-4 w-4" /></button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => onToggle && onToggle(o)}
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border ${inc ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                      data-testid={`evidence-toggle-${o.id}`}
                    >
                      {inc ? <><Check className="h-3 w-3" /> Included</> : <><Plus className="h-3 w-3" /> Include</>}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Physical / GPS ───────────────────────────────────────────────────────────
export const PhysicalGpsSection = ({ data, showMatches = true }) => {
  if (!data) return <EmptyState message="No GPS data recorded during this review period." />;
  const d = data;
  return (
    <div className="space-y-5" data-testid="section-gps">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="GPS Matches" value={d.sessions} testId="gps-sessions" />
        <StatCard label="Best Top Speed" value={d.best_top_speed_kmh} unit="km/h" testId="gps-topspeed" />
        <StatCard label="Avg Distance / Min" value={d.avg_distance_per_minute} unit="m" testId="gps-dpm" />
        <StatCard label="Avg HSR / Min" value={d.avg_hsr_per_minute} unit="m" testId="gps-hsr" />
      </div>
      {d.top_speed_benchmark && (
        <DevelopmentTargetBar comparison={d.top_speed_benchmark} label="Top Speed vs Development Target" data-testid="gps-target-bar" />
      )}
      {showMatches && d.match_by_match?.length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <summary className="cursor-pointer text-sm font-medium text-slate-600">GPS match detail ({d.match_by_match.length})</summary>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400">
                <th className="py-1 pr-3">Date</th><th className="py-1 pr-3">Dist (m)</th><th className="py-1 pr-3">Top (km/h)</th>
                <th className="py-1 pr-3">Dist/min</th><th className="py-1 pr-3">Sprints</th><th className="py-1 pr-3">Load</th><th className="py-1">Source</th>
              </tr></thead>
              <tbody>
                {d.match_by_match.map((m, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1 pr-3 text-slate-700">{fmtDate(m.activity_date)}</td>
                    <td className="py-1 pr-3">{fmtNum(m.total_distance_m)}</td>
                    <td className="py-1 pr-3">{fmtNum(m.max_speed_kmh)}</td>
                    <td className="py-1 pr-3">{fmtNum(m.distance_per_minute, 1)}</td>
                    <td className="py-1 pr-3">{fmtNum(m.sprint_count)}</td>
                    <td className="py-1 pr-3">{fmtNum(m.player_load)}</td>
                    <td className="py-1 text-slate-400">{m.provider || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
};

// ── Fitness ───────────────────────────────────────────────────────────────────
export const FitnessSection = ({ data }) => {
  if (!data) return <EmptyState message="No fitness assessment recorded for this athlete." />;
  const tests = data.tests || [];
  if (tests.length === 0) return <EmptyState message="No fitness assessment recorded for this athlete." />;
  return (
    <div className="space-y-4" data-testid="section-fitness">
      {data.outside_period && (
        <OutsidePeriodBanner date={(tests.find((t) => t.outside_period) || {}).assessment_date} kind="fitness assessment" />
      )}
      {tests.map((t, i) => {
        const cur = t.period_latest?.value;
        const prev = t.period_previous?.value;
        const pb = t.personal_best?.value ?? t.personal_best;
        const trend = (cur != null && prev != null)
          ? { label: cur === prev ? 'No change' : (t.direction === 'lower_better' ? (cur < prev ? 'Improving' : 'Declining') : (cur > prev ? 'Improving' : 'Declining')) }
          : null;
        return (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`fitness-${t.test_type}`}>
            <div className="mb-2 flex items-center justify-between">
              <div className="font-medium text-slate-800">{t.test_type}</div>
              <div className="text-xs text-slate-400">{t.category}{t.unit ? ` · ${t.unit}` : ''}</div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <StatCard label="Current" value={cur} unit={t.unit} testId={`fitness-cur-${t.test_type}`} />
              <StatCard label="Previous" value={prev} unit={t.unit} testId={`fitness-prev-${t.test_type}`} />
              <StatCard label="Personal Best" value={typeof pb === 'number' ? pb : (pb ?? null)} unit={t.unit} testId={`fitness-pb-${t.test_type}`} />
            </div>
            {t.benchmark && <DevelopmentTargetBar comparison={t.benchmark} trend={trend} label="vs Development Target" data-testid={`fitness-target-${t.test_type}`} />}
            {!t.benchmark && trend && <div className="text-xs text-slate-500">Trend: <strong>{trend.label}</strong></div>}
          </div>
        );
      })}
    </div>
  );
};

// ── Biometrics ────────────────────────────────────────────────────────────────
const BIO_GROUPS = [
  ['Body Profile', [['height_cm', 'Height', 'cm'], ['weight_kg', 'Weight', 'kg'], ['bmi', 'BMI', '']]],
  ['Composition', [['fat_mass_pct', 'Body Fat', '%'], ['muscle_mass_pct', 'Muscle Mass', '%']]],
  ['Vitals', [['resting_heart_rate', 'Resting HR', 'bpm'], ['blood_pressure_systolic', 'BP Systolic', ''], ['blood_pressure_diastolic', 'BP Diastolic', ''], ['blood_o2_saturation', 'O₂ Sat', '%']]],
];
export const BiometricsSection = ({ data }) => {
  if (!data) return <EmptyState message="No biometrics assessment recorded for this athlete." />;
  const latest = data.latest || {};
  const benchmarks = data.benchmarks || {};
  return (
    <div className="space-y-5" data-testid="section-biometrics">
      {data.outside_period && <OutsidePeriodBanner date={data.assessment_date || latest.date} kind="biometrics assessment" />}
      {BIO_GROUPS.map(([group, fields]) => {
        const rows = fields.filter(([k]) => latest[k] !== null && latest[k] !== undefined);
        if (rows.length === 0) return null;
        return (
          <div key={group}>
            <h4 className="mb-1.5 text-sm font-semibold text-slate-700">{group}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {rows.map(([k, l, u]) => <StatCard key={k} label={l} value={latest[k]} unit={u} testId={`bio-${k}`} />)}
            </div>
          </div>
        );
      })}
      {Object.keys(benchmarks).length > 0 && (
        <div className="space-y-2">
          {Object.entries(benchmarks).map(([k, cmp]) => (
            <DevelopmentTargetBar key={k} comparison={cmp} label={`${k.toUpperCase()} vs Development Target`} data-testid={`bio-target-${k}`} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Attendance & Availability ──────────────────────────────────────────────────
export const AttendanceSection = ({ data }) => {
  if (!data || !data.overall) return <EmptyState message="No finalized attendance sessions are available for this review period." />;
  const o = data.overall;
  if (!o.marked) return <EmptyState message="No finalized attendance sessions are available for this review period." />;
  const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
  return (
    <div className="space-y-4" data-testid="section-attendance">
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 px-4 py-4" data-testid="att-rate">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-medium">Attendance Rate</div>
          <div className="mt-1 text-3xl font-semibold text-emerald-800">{pct(o.attendance_rate)}</div>
          <div className="text-xs text-emerald-700/70">{o.attended} of {o.marked} sessions attended</div>
        </div>
        <div className="rounded-2xl border border-sky-200 bg-sky-50/50 px-4 py-4" data-testid="att-availability">
          <div className="text-xs uppercase tracking-wide text-sky-700 font-medium">Full Training Availability</div>
          <div className="mt-1 text-3xl font-semibold text-sky-800">{pct(o.availability_rate)}</div>
          <div className="text-xs text-sky-700/70">{o.available_full} of {o.marked} fully available</div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Justified Absence" value={o.justified_absence} testId="att-justified" />
        <StatCard label="Unexcused Absence" value={o.unexcused_absence} testId="att-unexcused" />
        <StatCard label="Restricted Participation" value={o.restricted} testId="att-restricted" />
        <StatCard label="Medical Absence" value={o.medical_unavailable} testId="att-medical" />
      </div>
    </div>
  );
};

// ── Mental / PDP / Injury (backend-whitelisted only) ─────────────────────────────
export const MentalSection = ({ data }) => {
  if (!data || !(data.entries || []).length) return <EmptyState message="No shareable mental development notes during this review period." />;
  return (
    <div className="space-y-2" data-testid="section-mental">
      {data.entries.map((e) => (
        <div key={e.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
          <div className="flex items-center justify-between">
            <span className="font-medium text-slate-800">{e.title || 'Development note'}</span>
            {e.status && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 capitalize">{e.status}</span>}
          </div>
          <div className="mt-0.5 text-xs text-slate-400">{e.entry_type}{e.due_date ? ` · due ${fmtDate(e.due_date)}` : ''}</div>
        </div>
      ))}
    </div>
  );
};

export const PdpSection = ({ data }) => {
  if (!data || !(data.goals || []).length) return <EmptyState message="No development plan goals available." />;
  return (
    <div className="space-y-2" data-testid="section-pdp">
      {data.goals.map((g) => (
        <div key={g.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-slate-800">{g.title}</span>
            {g.status && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 capitalize">{g.status}</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-400">
            {g.category && <span>{g.category}</span>}
            {g.cycle_name && <span>· {g.cycle_name}</span>}
            {g.progress != null && <span>· {g.progress}%</span>}
            {g.target_date && <span>· target {fmtDate(g.target_date)}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

export const InjurySection = ({ data }) => {
  if (!data || !(data.injuries || []).length) return <EmptyState message="No injury or rehabilitation records during this review period." />;
  return (
    <div className="space-y-2" data-testid="section-injury">
      {data.injuries.map((inj) => (
        <div key={inj.id} className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-slate-800">{inj.injury_type || 'Injury'}{inj.body_area ? ` · ${inj.body_area}` : ''}</span>
            {inj.status && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 capitalize">{inj.status}</span>}
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-400">
            {inj.severity && <span>Severity: {inj.severity}</span>}
            {inj.date_of_injury && <span>· {fmtDate(inj.date_of_injury)}</span>}
            {inj.clearance_status && <span>· Clearance: {inj.clearance_status}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};
